import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Shield, ArrowRightLeft, CheckSquare, Calendar as CalendarIcon, FileText, UserPlus, Clipboard, Trophy, UserCog, LogOut, Briefcase, Clock, Handshake, DollarSign, MessageSquare, Mail } from 'lucide-react';

const SidebarClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const day = now.toLocaleDateString('en-GB', { weekday: 'long' });
  const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return (
    <div className="px-6 py-4 border-b border-white/5">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4 text-white" strokeWidth={1.5} />
        <span className="text-lg font-bold tabular-nums text-white tracking-wide">{time}</span>
      </div>
      <p className="text-xs text-zinc-400">{day}, {date}</p>
    </div>
  );
};

const Layout = ({ user, onLogout }) => {
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Players', path: '/players', icon: Users },
    { name: 'Coaches', path: '/staff', icon: Briefcase },
    { name: 'Clubs', path: '/clubs', icon: Shield },
    { name: 'Club Meetings', path: '/club-meetings', icon: Handshake },
    { name: 'Tasks', path: '/tasks', icon: CheckSquare },
    { name: 'Calendar', path: '/calendar', icon: CalendarIcon },
    { name: 'Agency Fees', path: '/agency-fees', icon: DollarSign },
    { name: 'Messages', path: '/messages', icon: MessageSquare },
    { name: 'Player Tracker', path: '/client-trackers', icon: UserPlus },
    { name: 'Club Requests', path: '/club-requests', icon: Clipboard },
    { name: 'Fixtures Diary', path: '/fixtures-diary', icon: CalendarIcon },
    { name: 'Links', path: '/fixtures', icon: Trophy },
  ];

  const adminNavigation = user?.role === 'admin' ? [
    { name: 'User Management', path: '/user-management', icon: UserCog },
    { name: 'Email Settings', path: '/email-settings', icon: Mail }
  ] : [];

  return (
    <div className="min-h-screen bg-[#09090B] noise-texture">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 h-full w-64 bg-[#18181B] border-r border-white/5 hidden md:flex flex-col z-50">
        <div className="p-6 border-b border-white/5">
          <h1 className="text-2xl font-bold uppercase tracking-tight text-white">DC Sports & Talent</h1>
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Players &nbsp;|&nbsp; Partnerships &nbsp;|&nbsp; Performance</p>
        </div>
        <SidebarClock />
        
        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white text-black font-bold'
                    : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="uppercase tracking-wide text-sm">{item.name}</span>
              </Link>
            );
          })}
          {adminNavigation.length > 0 && (
            <>
              <div className="h-px bg-white/10 my-4"></div>
              {adminNavigation.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-white text-black font-bold'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    <span className="uppercase tracking-wide text-sm">{item.name}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="mb-3 px-4">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Logged in as</p>
            <p className="text-sm font-medium text-white">{user.full_name}</p>
            <p className="text-xs text-zinc-500">{user.role}</p>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-4 py-3 text-zinc-400 hover:bg-white/5 hover:text-white rounded-lg transition-all duration-200"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-zinc-950/90 backdrop-blur-lg border-t border-white/10 md:hidden z-50">
        <div className="flex items-center overflow-x-auto py-2 px-2 gap-1 scrollbar-hide">
          {navigation.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-3 py-2 flex-shrink-0 ${
                  isActive ? 'text-white' : 'text-zinc-400'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-[10px] uppercase whitespace-nowrap">{item.name}</span>
              </Link>
            );
          })}
          {adminNavigation.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-3 py-2 flex-shrink-0 ${
                  isActive ? 'text-white' : 'text-zinc-400'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-[10px] uppercase whitespace-nowrap">{item.name}</span>
              </Link>
            );
          })}
          <button
            onClick={onLogout}
            className="flex flex-col items-center gap-1 px-3 py-2 text-zinc-400 flex-shrink-0"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] uppercase whitespace-nowrap">Logout</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pb-20 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;